import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, CheckCircle2, ChevronDown, Languages, LogOut, Menu, PanelLeftClose, PanelLeftOpen, ShieldCheck, UserRound } from 'lucide-react';
import RoleNotifications from '../notifications/RoleNotifications';
import GlobalSearch from '../search/GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supportedLanguages } from '../../i18n/config';
import '../../styles/components/profile-media.css';

const roleTranslationKeys = {
  SUPER_ADMIN: 'roles.superAdmin',
  RESTAURANT_MANAGER: 'roles.restaurantManager',
  MANAGER: 'roles.restaurantManager',
  SERVEUR: 'roles.serveur',
  WAITER: 'roles.serveur',
  CUISINE: 'roles.cuisine',
  KITCHEN: 'roles.cuisine',
  CUISINER: 'roles.cuisine',
  CUISINIER: 'roles.cuisine',
  CUISINI: 'roles.cuisine',
};

const normalizeRole = (role) => String(role || '').trim().toUpperCase().replace(/[\s-]/g, '_');
const getRoleName = (role) => {
  if (typeof role === 'string' || typeof role === 'number') return String(role);
  return role?.name || role?.slug || role?.code || role?.value || '';
};
const getCurrentRoles = (user) => {
  const sources = [user?.roles, user?.role, user?.data?.roles, user?.data?.role, user?.user?.roles, user?.user?.role, user?.account?.roles, user?.account?.role, user?.profile?.roles, user?.profile?.role];
  return sources.flatMap((source) => (Array.isArray(source) ? source : [source])).map(getRoleName).filter(Boolean);
};
const isSalesRole = (role) => ['salesrepresentative', 'salesrepresentatives', 'marketing', 'marketingmanager', 'responsablemarketing'].includes(String(role).toLowerCase().replace(/[\s_-]/g, ''));
const getInitials = (name = '') => name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]?.toUpperCase()).join('') || 'U';

function Header({ collapsed = false, onToggleSidebar, onToggleMobileSidebar }) {
  const accountRef = useRef(null);
  const languageRef = useRef(null);
  const { t, language, changeLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const userName = user?.name || t('header.profile');
  const userEmail = user?.email || '';
  const userInitials = getInitials(userName);
  const userAvatarUrl = user?.avatar_url || '';
  const currentRoles = getCurrentRoles(user);
  const salesRepresentative = currentRoles.some(isSalesRole) && !currentRoles.some((role) => ['SUPERADMIN', 'SUPER_ADMIN', 'SUPERADMINISTRATOR'].includes(normalizeRole(role)));
  const mainRole = currentRoles[0] || '';
  const roleKey = roleTranslationKeys[normalizeRole(mainRole)];
  const roleLabel = salesRepresentative ? 'Sales Representative' : roleKey ? t(roleKey) : mainRole;
  const selectedLanguage = useMemo(() => supportedLanguages.find((item) => item.code === language) || supportedLanguages[0], [language]);

  const handleLanguageChange = (nextLanguage) => {
    changeLanguage(nextLanguage);
    setLanguageOpen(false);
  };
  const handleLogout = async () => {
    setAccountOpen(false);
    await logout();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) setAccountOpen(false);
      if (languageRef.current && !languageRef.current.contains(event.target)) setLanguageOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setAccountOpen(false);
        setLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <header className="dashboard-header">
      <div className="dashboard-header__start">
        <button type="button" className="dashboard-header__icon-button dashboard-header__desktop-toggle" onClick={onToggleSidebar} aria-label={t('header.toggleSidebar')}>
          {collapsed ? <PanelLeftOpen size={21} /> : <PanelLeftClose size={21} />}
        </button>
        <button type="button" className="dashboard-header__icon-button dashboard-header__mobile-toggle" onClick={onToggleMobileSidebar} aria-label={t('header.openSidebar')}>
          <Menu size={22} />
        </button>
        {!salesRepresentative && <GlobalSearch />}
      </div>
      <div className="dashboard-header__actions">
        <div ref={languageRef} className={`dashboard-language ${languageOpen ? 'dashboard-language--open' : ''}`}>
          <button type="button" className="dashboard-language__trigger" onClick={() => setLanguageOpen((currentValue) => !currentValue)} aria-label={t('header.language')} aria-expanded={languageOpen}>
            <Languages size={18} />
            <span>{selectedLanguage?.label}</span>
            <ChevronDown size={16} />
          </button>
          {languageOpen && (
            <div className="dashboard-language__menu">
              {supportedLanguages.map((item) => {
                const active = item.code === language;
                return (
                  <button key={item.code} type="button" className={`dashboard-language__item ${active ? 'dashboard-language__item--active' : ''}`} onClick={() => handleLanguageChange(item.code)}>
                    <span>{item.label}</span>
                    {active && <Check size={15} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {!salesRepresentative && <RoleNotifications />}
        <div className="dashboard-account" ref={accountRef}>
          <button type="button" className={`dashboard-account__trigger ${accountOpen ? 'dashboard-account__trigger--open' : ''}`} onClick={() => setAccountOpen((currentValue) => !currentValue)} aria-label={t('header.profile')} aria-expanded={accountOpen}>
            <span className="dashboard-account__avatar">{userAvatarUrl ? <img src={userAvatarUrl} alt={userName} /> : userInitials}</span>
            <span className="dashboard-account__identity"><strong>{userName}</strong><small>{roleLabel}</small></span>
            <ChevronDown size={16} />
          </button>
          {accountOpen && (
            <div className="dashboard-account__menu">
              <div className="dashboard-account__top">
                <span className="dashboard-account__avatar dashboard-account__avatar--large">{userAvatarUrl ? <img src={userAvatarUrl} alt={userName} /> : userInitials}</span>
                <div><strong>{userName}</strong><small>{userEmail}</small></div>
              </div>
              <div className="dashboard-account__status">
                <span><ShieldCheck size={16} />{roleLabel}</span>
                <em><CheckCircle2 size={14} />{t('common.active')}</em>
              </div>
              <div className="dashboard-account__divider" />
              <Link to="/account-settings" className="dashboard-account__item" onClick={() => setAccountOpen(false)}>
                <UserRound size={17} /><span>{t('header.profile')}</span>
              </Link>
              <button type="button" className="dashboard-account__item dashboard-account__item--logout" onClick={handleLogout}>
                <LogOut size={17} /><span>{t('auth.logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
